import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import Select from "react-select";
import CloseButtonX from "../CloseButtonX/CloseButtonX";
import "../ScopeStackModal/ScopeStackModal.css";
import ProjectResourceSelect from "../ProjectResourceSelect/ProjectResourceSelect";
import useProjectResourceOptions from "../ProjectResourceSelect/useProjectResourceOptions";
import { SelectOption, useServiceValueOptions } from "./useServiceValueOptions";

export type ServiceValueField =
  | "resource"
  | "phase"
  | "serviceCategory"
  | "quantity"
  | "hours";

export interface ServiceValues {
  // A typed resource value from ProjectResourceSelect ("project-resources-1",
  // "resources-7", "resource-rates-9"), sent as the project-resource id.
  resource?: string;
  phase?: string;
  serviceCategory?: string;
  quantity?: number;
  hours?: number;
}

export interface ServiceValuesModalProps {
  show: boolean;
  projectId: string;
  title: string;
  applyLabel: string;
  initialValues?: ServiceValues;
  fields?: ServiceValueField[];
  requiredFields?: ServiceValueField[];
  onApply: (values: ServiceValues) => Promise<void>;
  onClose: () => void;
}

const ALL_FIELDS: ServiceValueField[] = [
  "resource",
  "phase",
  "serviceCategory",
  "quantity",
  "hours",
];

const LABELS: Record<ServiceValueField, string> = {
  resource: "Resource",
  phase: "Phase",
  serviceCategory: "Service Category",
  quantity: "Quantity",
  hours: "Hours",
};

type FormState = Record<ServiceValueField, string>;

const toFormState = (values: ServiceValues = {}): FormState => ({
  resource: values.resource ?? "",
  phase: values.phase ?? "",
  serviceCategory: values.serviceCategory ?? "",
  quantity: values.quantity === undefined ? "" : String(values.quantity),
  hours: values.hours === undefined ? "" : String(values.hours),
});

const parseNumber = (text: string): number | undefined => {
  const trimmed = text.trim();
  if (trimmed === "") return undefined;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : undefined;
};

const isNumberField = (f: ServiceValueField) =>
  f === "quantity" || f === "hours";

const ServiceValuesModal: React.FC<ServiceValuesModalProps> = ({
  show,
  projectId,
  title,
  applyLabel,
  initialValues,
  fields = ALL_FIELDS,
  requiredFields = [],
  onApply,
  onClose,
}) => {
  const options = useServiceValueOptions(projectId, show);
  const resourceOptions = useProjectResourceOptions(projectId);
  const [form, setForm] = useState<FormState>(() => toFormState(initialValues));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const initialRef = useRef(initialValues);
  initialRef.current = initialValues;

  // Reset from initialValues each time the dialog opens.
  useEffect(() => {
    if (show) {
      setForm(toFormState(initialRef.current));
      setError(null);
    }
  }, [show]);

  const optionsFor: Record<string, SelectOption[]> = {
    resource: resourceOptions.options.flatMap((g) => g.options),
    phase: options.phases,
    serviceCategory: options.serviceCategories,
  };

  // A select value only counts as set if it matches a loaded option.
  const hasValue = (f: ServiceValueField): boolean => {
    if (isNumberField(f)) return parseNumber(form[f]) !== undefined;
    return form[f] !== "" && optionsFor[f].some((o) => o.value === form[f]);
  };

  const missingRequired = requiredFields.some(
    (f) => fields.includes(f) && !hasValue(f)
  );

  const handleApply = async () => {
    const values: ServiceValues = {};
    fields.forEach((f) => {
      if (isNumberField(f)) {
        const n = parseNumber(form[f]);
        if (n !== undefined) (values as any)[f] = n;
      } else if (form[f] !== "") {
        (values as any)[f] = form[f];
      }
    });
    setSaving(true);
    setError(null);
    try {
      await onApply(values);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  const renderField = (f: ServiceValueField) => {
    const id = `service-values-${f}`;
    return (
      <Form.Group className="formGroup" key={f}>
        <Form.Label htmlFor={id}>{LABELS[f]}</Form.Label>
        {f === "resource" ? (
          <ProjectResourceSelect
            id={id}
            label={LABELS[f]}
            hideLabel
            projectId={projectId}
            isClearable
            value={form[f] || null}
            onChange={(o) => setForm({ ...form, [f]: o ? o.value : "" })}
          />
        ) : isNumberField(f) ? (
          <Form.Control
            id={id}
            type="number"
            min={0}
            step="any"
            value={form[f]}
            onChange={(e) => setForm({ ...form, [f]: e.target.value })}
          />
        ) : (
          <Select
            inputId={id}
            options={optionsFor[f]}
            isLoading={options.isLoading}
            isClearable
            value={optionsFor[f].find((o) => o.value === form[f]) ?? null}
            onChange={(o) => setForm({ ...form, [f]: o ? o.value : "" })}
          />
        )}
      </Form.Group>
    );
  };

  return (
    <Modal show={show} onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title className="ssModalTitle">{title}</Modal.Title>
        <CloseButtonX onClose={onClose} />
      </Modal.Header>
      <Modal.Body className="ssModalBody">
        {error && <Alert variant="danger">{error}</Alert>}
        {ALL_FIELDS.filter((f) => fields.includes(f)).map(renderField)}
      </Modal.Body>
      <Modal.Footer className="ssModalFooter">
        <Button variant="secondary" className="btnSeawhite" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          className="seafoamBgButton"
          onClick={handleApply}
          disabled={saving || missingRequired}
        >
          {saving && (
            <Spinner
              as="span"
              animation="border"
              size="sm"
              role="status"
              aria-hidden="true"
              className="mr-2"
            />
          )}
          {applyLabel}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ServiceValuesModal;
